import React, { useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from 'classnames';
import dateformat from "dateformat";

import Thumbnail from "src/components/thumbnail";
import LinesEllipsis from "react-lines-ellipsis";
import Reveal from "src/components/animation";
import { FileService } from "src/services/file.service";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';

import styles from './styles.module.scss';
import Tooltip from "src/components/tooltip";
import { DealRoomAsset } from "src/types/dealRoom";
import { AssetsLayoutEnum } from "../../types";
import { Col, Row } from "react-bootstrap";
import { DealRoomContext } from "../context";
import { useSelector } from "react-redux";

interface AssetCardProps {
  asset: DealRoomAsset;
  editable: boolean;
  activeAssetLayout: AssetsLayoutEnum;

  // eslint-disable-next-line no-unused-vars
  onRemove: (asset: DealRoomAsset) => void;

  // eslint-disable-next-line no-unused-vars
  onClick: (asset: DealRoomAsset) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({ asset, activeAssetLayout, editable, onRemove, onClick }) => {
  const assetItem = asset.asset;


  const { menuState , id} = React.useContext(DealRoomContext);
  const editing = menuState.primaryAction === 'edit';
  const assetAnalyticsArr = useSelector((state: any) => _.get(state, `dealRooms.dealRoom['${id}'].assetAnalytics`, []));
  const totalTimeSpent = _.get(assetAnalyticsArr, [asset.id, 'timeSpent'], undefined);

  const fileType = useMemo(() => {
    return FileService.getFileType(assetItem.metadata);
  }, [assetItem]);

  const fileSize = useMemo(() => {
    const size = _.get(assetItem, 'metadata.content_length');
    if (!size) return null;
    return FileService.formatSize(size);
  }, [assetItem]);

  const internal = !_.get(assetItem, 'settings.share', false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: asset.id,
    disabled: !editable,
    data: {
      asset: asset,
      type: 'asset'
    }
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove(asset);
    }
  }

  const handleClick = (e) => {
    e.preventDefault();
    if (!editable && !editing && onClick) {
      onClick(asset);
    }
  }

  if (activeAssetLayout === AssetsLayoutEnum.LIST) {

    return (
      <div className={cx(styles.listCard, {
        [styles.editDisabled]: editing && !editable,
        [styles.dragging]: isDragging
      })}
        style={style} ref={setNodeRef} {...listeners} {...attributes}
        onClick={handleClick}
      >
        <Row>
          <Col xs={4}>
            <div className={styles.iconContainer}>

              <div className={styles.icon}>

                {internal &&
                  <div className={styles.internalIcon}>
                    <Tooltip content="Internal use only" placement="top">
                      <i className="paperflite-lock" />
                    </Tooltip>
                  </div>
                }

                {editable && <div className={styles.deleteIcon} onClick={handleRemove}>
                  <i className="paperflite-close" />
                </div>}


                {editing && <div className={styles.overlay}></div>}
                <Thumbnail media={assetItem.icon} defaultImage="/images/assets/default_asset.jpg" />
              </div>

              <div className={styles.nameContainer}>
                <p style={{ maxWidth: '200px' }} className={cx(styles.name, styles.truncatedText)}>
                  {assetItem.name || 'Untitled'}
                </p>
                <p className={styles.lastViewed}>{`Last updated on ${dateformat(_.get(assetItem, 'modifiedDate'), 'dd mmm yyyy')}`}</p>
              </div>
            </div>

          </Col>
          <Col className={styles.cell} xs={3}>
            {fileType} <span className={styles.divider} />
            {_.get(assetItem, "metadata.read_time") && (
              <>
                {`${Math.ceil(_.get(assetItem, "metadata.read_time") / 60)} Mins Read`}
                <span className={styles.divider} />
              </>
            )}
            {fileSize}
          </Col>
          <Col className={styles.cell} xs={2}>
            <div style={{ maxWidth: '200px' }} className={styles.truncatedText}>{_.get(assetItem, 'author.firstName')}</div>
          </Col>
          <Col className={styles.cell} xs={2}>
            {dateformat(_.get(assetItem, "createdDate"), 'dd mmm yyyy')}
          </Col>
          {/* <Col className={styles.cell} xs={2}>
            <div className={styles.viewedBy}>
              {_.times(3, (index) => (
                <div className={styles.avatar}>
                  <img src={"https://plus.unsplash.com/premium_photo-1689551670902-19b441a6afde?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8cmFuZG9tJTIwcGVvcGxlfGVufDB8fDB8fHww"} />
                </div>
              ))}
            </div>
            + 4 more people
          </Col> */}
          <Col className={styles.cell} xs={1}>
            { totalTimeSpent !== undefined
                ? FileService.formatDuration(totalTimeSpent, 'short')
                : "-" }
          </Col>
        </Row>
      </div>
    )

  }


  return (
    <div className={cx(styles.card, {
      [styles.editable]: editable,
      [styles.dragging]: isDragging
    })}
      style={style} ref={setNodeRef} {...listeners} {...attributes}
      onClick={handleClick}
    >
      <div className={styles.cardThumbnail}>

        <div className={styles.icon}>
          <Thumbnail media={assetItem.icon} defaultImage="/images/assets/default_asset.jpg" />
          <span className={styles.fileType}>
            {fileType}
          </span>
        </div>

        <div className={styles.overlay}></div>

        {internal &&
          <div className={styles.indicators}>
            <Tooltip content="Internal use only" placement="auto">
              <i className="paperflite-lock" />
            </Tooltip>
          </div>
        }

        <Reveal show={editable}>
          <button className={styles.remove} onClick={handleRemove}>
            <i className="fa-regular fa-circle-xmark"></i>
          </button>
        </Reveal>
      </div>

      <div className={styles.cardContent}>
        <p style={{ maxWidth: '125px' }} className={cx(styles.name, styles.truncatedText)}>{assetItem.name || 'Untitled'}</p>
        <div className={styles.meta}>
          <span>
            {fileSize && fileSize}
          </span>
          {_.get(assetItem, "metadata.read_time") && (
            <>
              <span className={styles.divider} />
              {`${Math.ceil(_.get(assetItem, "metadata.read_time") / 60)} Mins Read`}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


const PlaceholderAssetCard: React.FC<any> = ({ key }) => {
  return (
    <div key={key} className={cx(styles.card, styles.placeholder)}>
      <div className={cx(styles.icon, styles.block)}></div>
    </div>
  );
}

const DraggingAssetCard: React.FC<{ asset: DealRoomAsset, overSection: string | null, activeAssetLayout: AssetsLayoutEnum }> = ({ asset, overSection, activeAssetLayout }) => {
  const assetItem = asset.asset;
  const { id } = React.useContext(DealRoomContext);
  const assetAnalyticsArr = useSelector((state: any) => _.get(state, `dealRooms.dealRoom['${id}'].assetAnalytics`, []));
  const totalTimeSpent = _.get(assetAnalyticsArr, [asset.id, 'timeSpent'], undefined);

  const fileType = useMemo(() => {
    return FileService.getFileType(assetItem.metadata);
  }, [assetItem]);

  const fileSize = useMemo(() => {
    const size = _.get(assetItem, 'metadata.content_length');
    if (!size) return null;
    return FileService.formatSize(size);
  }, [assetItem]);

  if (activeAssetLayout === AssetsLayoutEnum.LIST) {
    return (
      <div className={cx(styles.listCard, { [styles.overSectionList]: overSection })}>
        <Row className={styles.innerContainer}>
          <Col xs={4}>
            <div className={styles.iconContainer}>

              <div className={styles.icon}>
                <Thumbnail media={assetItem.icon} defaultImage="/images/assets/default_asset.jpg" />
              </div>

              <div className={styles.nameContainer}>
                <p className={cx(styles.name, styles.truncatedText)}>
                  {assetItem.name || 'Untitled'}
                </p>
                <p className={styles.lastViewed}>{`Last updated on ${dateformat(_.get(assetItem, 'modifiedDate'), 'dd mmm yyyy')}`}</p>
              </div>
            </div>

          </Col>
          <Col className={styles.cell} xs={3}>
            {fileType} <span className={styles.divider} />
            {_.get(assetItem, "metadata.read_time") && (
              <>
                {`${Math.ceil(_.get(assetItem, "metadata.read_time") / 60)} Mins Read`}
                <span className={styles.divider} />
              </>
            )}
            {fileSize}
          </Col>
          <Col className={styles.cell} xs={2}>
            {_.get(assetItem, 'author.firstName')}
          </Col>
          <Col className={styles.cell} xs={2}>
            {dateformat(_.get(assetItem, "createdDate"), 'dd mmm yyyy')}
          </Col>
          {/* <Col className={styles.cell} xs={2}>
            <div className={styles.viewedBy}>
              {_.times(3, (index) => (
                <div className={styles.avatar}>
                  <img src={"https://plus.unsplash.com/premium_photo-1689551670902-19b441a6afde?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8cmFuZG9tJTIwcGVvcGxlfGVufDB8fDB8fHww"} />
                </div>
              ))}
            </div>
            + 4 more people
          </Col> */}
          <Col className={styles.cell} xs={1}>
                { totalTimeSpent !== undefined
                ? FileService.formatDuration(totalTimeSpent, 'short')
                : "-" }
          </Col>
        </Row>
        <div className={styles.message}>Add to section</div>
      </div>
    )
  }

  return (
    <div className={cx(styles.card, { [styles.overSection]: overSection })}>
      <div className={styles.innerContainer}>
        <div className={styles.cardThumbnail}>
          <div className={styles.icon}>
            <Thumbnail media={assetItem.icon} defaultImage="/images/assets/default_asset.jpg" />
            <span className={styles.fileType}>
              {fileType}
            </span>
          </div>
        </div>
        <div className={styles.cardContent}>
          <LinesEllipsis className={styles.name} text={assetItem.name} maxLine='2' />
          <div className={styles.meta}> {fileSize && `${fileSize}`}</div>
        </div>
      </div>
      <div className={styles.message}>Add to section</div>
    </div>
  );
}

export { AssetCard, PlaceholderAssetCard, DraggingAssetCard };